import { useState, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { apiService } from "@/lib/api-functions/api-service";
import { processLibraryItems, groupItemsByBlueprint } from "@/lib/integrations/libraryProcessorShared";
import { createCustomDataPackage, downloadZipBlob } from "@/lib/integrations/libraryProcessorCustom";
import { getPresetTemplates } from "@/lib/integrations/exportTemplatePresets";
import { getAvailablePlatformsForGame, getDefaultPlatformForGame } from "@/lib/utils/platformMapping";
import { getFolderPath } from "@/lib/stores/folderStore";
import { fetchDocumentItems } from "@/lib/stores/documentStore";
import { ExportTemplate, ExportTemplatesConfig, NamingConvention } from "@/types/exportTemplates";
import { downloadBlob } from "./utils";

interface Game {
  id: string;
  name: string;
  company_id?: string;
  settings?: any;
}

interface UseDataPackageExportProps {
  glossaryBlueprints: any[];
  types: any[];
  tags: any[];
  allItems: any[];
}

export interface PendingExport {
  blob: Blob;
  filename: string;
  isZip: boolean;
  game: Game;
  platform: string;
  templateName: string;
  metadata: {
    itemCount: number;
    blueprintCount: number;
    dialogCount: number;
    documentCount: number;
    exportMode: string;
  };
}

interface PackageGenerationResult {
  blob: Blob;
  filename: string;
  isZip: boolean;
  itemCount: number;
  blueprintCount: number;
  dialogCount: number;
  documentCount: number;
  platform: string;
  templateName: string;
}

export function useDataPackageExport({
  glossaryBlueprints,
  types,
  tags,
  allItems
}: UseDataPackageExportProps) {
  const { toast } = useToast();
  const [isExportingPackage, setIsExportingPackage] = useState(false);
  const [isSavingToCloud, setIsSavingToCloud] = useState(false);
  const [pendingExport, setPendingExport] = useState<PendingExport | null>(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  const getActiveTemplate = (game: Game): ExportTemplate | undefined => {
    const exportConfig = game.settings?.exportTemplates as ExportTemplatesConfig | undefined;
    let activeTemplate: ExportTemplate | undefined;
    
    if (exportConfig?.activeTemplateId) {
      activeTemplate = exportConfig.templates?.find(t => t.id === exportConfig.activeTemplateId);
    }
    
    // Fallback to preset if no active template
    if (!activeTemplate) {
      const presets = getPresetTemplates();
      const defaultPlatform = getDefaultPlatformForGame(game);
      const availablePlatforms = getAvailablePlatformsForGame(game);
      
      activeTemplate = presets.find(p => p.platform === defaultPlatform);
      
      if (!activeTemplate) {
        for (const platform of availablePlatforms) {
          activeTemplate = presets.find(p => p.platform === platform);
          if (activeTemplate) break;
        }
      }
      
      if (!activeTemplate) {
        activeTemplate = presets[0];
      }
    }
    
    return activeTemplate;
  };

  // Core function to generate the export package
  const generatePackage = async (
    game: Game,
    dataPackageMode: 'all' | 'blueprint' | 'selection',
    selectedBlueprintId: string,
    namingConvention: NamingConvention
  ): Promise<PackageGenerationResult | null> => {
    // Determine which items to export
    let itemIds: string[] = [];
    
    if (dataPackageMode === 'all') {
      itemIds = [];
    } else if (dataPackageMode === 'blueprint') {
      if (!selectedBlueprintId) {
        toast({
          title: "Error",
          description: "Please select a blueprint",
          variant: "destructive",
        });
        return null;
      }
      
      const glossaryResult = await apiService.fetchObjects(game.id);
      if (glossaryResult.success && glossaryResult.data) {
        itemIds = glossaryResult.data
          .filter(item => item.blueprint_id === selectedBlueprintId)
          .map(item => item.id);
      }
    }

    // Note: We don't check for empty itemIds here anymore
    // Database exports (Unity, Roblox) can proceed with no items
    // The check will happen later after we fetch dialogs and determine the template type

    // Process items
    const processedItems = processLibraryItems(itemIds, game.id);

    // Group by blueprint
    const blueprintGroups = groupItemsByBlueprint(processedItems);
    
    // Get active template
    const activeTemplate = getActiveTemplate(game);
    
    if (!activeTemplate) {
      throw new Error('No export template configured');
    }

    // Fetch dialogs for export
    let dialogs: any[] = [];
    try {
      const dialogsResult = await apiService.fetchDialogs(game.id);
      if (dialogsResult.success && dialogsResult.data) {
        dialogs = dialogsResult.data;
        console.log(`📝 Fetched ${dialogs.length} dialogs for export`);
      }
    } catch (error) {
      console.warn('Failed to fetch dialogs, continuing without them:', error);
    }

    // Fetch documents for export (needed for document field references)
    let documents: any[] = [];
    try {
      documents = await fetchDocumentItems(game.id);
      console.log(`📄 Fetched ${documents.length} documents for export`);
    } catch (error) {
      console.warn('Failed to fetch documents, continuing without them:', error);
    }

    // Check if there's any content to export
    // For database exports (Unity, Roblox, etc.), allow export even with empty data
    // The export will generate valid empty files that can be compiled
    const isDatabaseExport = activeTemplate.platform === 'unity' || 
                            activeTemplate.platform === 'roblox' ||
                            activeTemplate.platform === 'json' ||
                            activeTemplate.id?.includes('database') ||
                            activeTemplate.id?.includes('runtime');
    
    if (!isDatabaseExport && processedItems.length === 0 && dialogs.length === 0) {
      toast({
        title: "No Items Found",
        description: "No glossary items or dialogs found to export",
        variant: "destructive",
      });
      return null;
    }
    
    console.log(`📦 Exporting: ${processedItems.length} items, ${dialogs.length} dialogs, ${documents.length} documents (database export: ${isDatabaseExport})`);

    // Create folder path resolver
    const folderPathResolver = (folderId: string | null | undefined): string => {
      return getFolderPath(game.id, 'library', folderId);
    };

    // Create package
    const packageResult = await createCustomDataPackage(
      blueprintGroups, 
      game.id, 
      game.name,
      activeTemplate,
      glossaryBlueprints,
      types,
      tags,
      allItems,
      folderPathResolver,
      namingConvention,
      activeTemplate.databaseSettings?.namespace || 'Grimoire.Data',
      activeTemplate.databaseSettings?.className || 'Database',
      game.settings,
      dialogs,
      documents
    );

    return {
      blob: packageResult.blob,
      filename: packageResult.filename,
      isZip: packageResult.isZip,
      itemCount: processedItems.length,
      blueprintCount: blueprintGroups.length,
      dialogCount: dialogs.length,
      documentCount: documents.length,
      platform: activeTemplate.platform,
      templateName: activeTemplate.name,
    };
  };

  // Handle direct download (original behavior)
  const handleDataPackageExport = async (
    game: Game,
    dataPackageMode: 'all' | 'blueprint' | 'selection',
    selectedBlueprintId: string,
    namingConvention: NamingConvention
  ) => {
    setIsExportingPackage(true);

    try {
      const result = await generatePackage(game, dataPackageMode, selectedBlueprintId, namingConvention);
      
      if (!result) {
        return;
      }

      // Download immediately
      if (result.isZip) {
        downloadZipBlob(result.blob, result.filename);
      } else {
        downloadBlob(result.blob, result.filename);
      }

      toast({
        title: "Package Exported Successfully",
        description: `Exported ${result.itemCount} item(s) in ${result.blueprintCount} blueprint(s) as ${result.filename}`,
      });

    } catch (error) {
      console.error("Error exporting data package:", error);
      toast({
        title: "Export Failed",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsExportingPackage(false);
    }
  };

  // Handle save to cloud - generates package and shows save dialog
  const handleSaveToCloud = async (
    game: Game,
    dataPackageMode: 'all' | 'blueprint' | 'selection',
    selectedBlueprintId: string,
    namingConvention: NamingConvention
  ) => {
    setIsSavingToCloud(true);

    try {
      const result = await generatePackage(game, dataPackageMode, selectedBlueprintId, namingConvention);
      
      if (!result) {
        return;
      }

      // Store as pending export and show save dialog
      const exportData: PendingExport = {
        blob: result.blob,
        filename: result.filename,
        isZip: result.isZip,
        game,
        platform: result.platform,
        templateName: result.templateName,
        metadata: {
          itemCount: result.itemCount,
          blueprintCount: result.blueprintCount,
          dialogCount: result.dialogCount,
          documentCount: result.documentCount,
          exportMode: dataPackageMode,
        },
      };

      setPendingExport(exportData);
      setShowSaveDialog(true);

    } catch (error) {
      console.error("Error preparing export for cloud save:", error);
      toast({
        title: "Export Failed",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsSavingToCloud(false);
    }
  };

  const closeSaveDialog = useCallback(() => {
    setShowSaveDialog(false);
    setPendingExport(null);
  }, []);

  return {
    isExportingPackage,
    isSavingToCloud,
    handleDataPackageExport,
    handleSaveToCloud,
    getActiveTemplate,
    // For versioning dialog
    pendingExport,
    showSaveDialog,
    setShowSaveDialog,
    closeSaveDialog,
  };
}
